import { useSearchParams } from 'react-router-dom'

import { useApiClient } from '@/api/ApiClientProvider'
import { AD_PROVIDER_LABELS, AD_PROVIDERS, type AdProvider, type AdvertisingOverview } from '@/api/advertisingOverview'
import { SectionFrame } from '@/app/pages'
import type { RouteDef } from '@/app/routes'
import { CoverageIndicator } from '@/components/CoverageIndicator'
import { DataQualityNotice } from '@/components/DataQualityNotice'
import { ModelSelect } from '@/components/ModelSelect'
import { TmtAttributedBadge } from '@/components/TmtAttributedBadge'
import { AdvertisingFreshnessBadge, ProviderAttributedBadge, SourceBadge } from '@/features/analytics/advertising/AdvertisingBadges'
import { ConnectMeta } from '@/features/analytics/advertising/AdvertisingStates'
import { DEFAULT_MODEL, useModel } from '@/features/analytics/advertising/advertisingQuery'
import { usePageFreshness } from '@/lib/freshness'
import { useFilterSearch, useGlobalFilters } from '@/lib/useGlobalFilters'
import { useUrlChoice } from '@/lib/useUrlChoice'

import { isAdvertisingFlagOff } from './errors'
import { KpiSection } from './KpiSection'
import { CampaignPerformanceCard } from './CampaignPerformanceCard'
import { CreativePlaceholderCard } from './CreativePlaceholderCard'
import { AllSalesTrendCard } from './AllSalesTrendCard'
import { ProviderReportedCard } from './ProviderReportedCard'
import { SpendRevenueTrendCard } from './SpendRevenueTrendCard'
import { TopCampaignsCard } from './TopCampaignsCard'
import { useAdvertisingOverview } from './overviewQuery'
import { ReconciliationPanel } from './ReconciliationPanel'
import '@/features/analytics/advertising/advertising.css'
import './advertising-overview.css'

/** The campaign report (`/analytics/advertising`) is "View all campaigns", with the same filters and model. */
function useViewAllHref(): string {
  const search = useFilterSearch()
  const [model] = useModel()
  const params = new URLSearchParams(search)
  if (model !== DEFAULT_MODEL) params.set('adv_model', model)
  const query = params.toString()
  return `/analytics/advertising${query ? `?${query}` : ''}`
}

/** Nothing imported and nothing to show: the fix is Integrations, as on the campaign report. */
function needsConnection(overview: AdvertisingOverview): boolean {
  const { asOf } = overview.meta
  return (!asOf.connected || asOf.mappedAccounts === 0) && Object.keys(overview.notAvailable).length > 0
}

/**
 * Marketing › Advertising: is paid working this period? Meta's spend and delivery, the
 * revenue and purchases TmT attributed to it under a selectable model, Meta's own
 * attributed figures beside them (never added), the trend, the best campaigns, the
 * campaign table and the Meta-vs-TmT reconciliation (blueprint 06 §6.4–6.6, 10 §10.6).
 * Each section has its own query, so one failing leaves the rest standing.
 */
export function AdvertisingOverviewPage({ route }: { route: RouteDef }) {
  const client = useApiClient()
  const [params] = useSearchParams()
  const { filters } = useGlobalFilters()
  const [provider, setProvider] = useUrlChoice<AdProvider>('adv_provider', AD_PROVIDERS, 'meta')
  const [model, setModel] = useModel()
  const overview = useAdvertisingOverview(filters, provider, model)
  const viewAllHref = useViewAllHref()
  const source = client.kind === 'http' ? 'live' : 'demo'
  usePageFreshness(overview.data ? { source, lastUpdatedAt: overview.data.meta.asOf.generatedAt } : undefined)

  const flagOff = isAdvertisingFlagOff(overview.error)
  const disconnected = overview.data ? needsConnection(overview.data) : false

  return (
    <SectionFrame route={route} actions={<SourceBadge />}>
      <div className="ci-stack">
        <div className="adv-controls">
          <label className="ci-field ci-field--inline">
            <span>Ad platform</span>
            <select value={provider} onChange={(event) => setProvider(event.target.value as AdProvider)}>
              {AD_PROVIDERS.map((option) => (
                <option key={option} value={option}>
                  {AD_PROVIDER_LABELS[option]}
                </option>
              ))}
            </select>
          </label>
          <ModelSelect value={model} onChange={setModel} />
          {overview.data ? <AdvertisingFreshnessBadge freshness={overview.data.meta.asOf} source={source} /> : null}
        </div>

        {overview.data && !flagOff ? (
          <div className="aov-labels" role="group" aria-label="Attribution and sources">
            <TmtAttributedBadge model={overview.data.meta.model} lookbackDays={overview.data.meta.lookbackDays} className="adv-tmt-badge" />
            <CoverageIndicator dataQuality={overview.data.dataQuality} />
            <ProviderAttributedBadge attributionWindows={overview.data.meta.attributionWindows} />
            <span className="aov-note">
              TmT-attributed figures are counted from your own orders under the selected model; Meta-reported figures are
              Meta’s. The two are shown side by side and never added together.
            </span>
          </div>
        ) : null}

        {overview.data && !flagOff ? <DataQualityNotice dataQuality={overview.data.dataQuality} /> : null}

        {flagOff ? (
          <ConnectMeta reason="not_entitled" />
        ) : disconnected ? (
          <ConnectMeta reason="not_connected" />
        ) : (
          <>
            <div className="aov-headline">
              <KpiSection query={overview} compare={filters.compare} focus={params.get('metric')} />
              <ProviderReportedCard query={overview} />
            </div>
            <div className="aov-charts">
              <SpendRevenueTrendCard filters={filters} provider={provider} model={model} />
              <TopCampaignsCard query={overview} model={model} />
            </div>
            <AllSalesTrendCard filters={filters} provider={provider} />
            <CampaignPerformanceCard query={overview} model={model} viewAllHref={viewAllHref} />
            <CreativePlaceholderCard />
            <ReconciliationPanel filters={filters} model={model} />
          </>
        )}
      </div>
    </SectionFrame>
  )
}
